import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link } from "@tanstack/react-router";
import type { CSSProperties, ReactNode } from "react";
import { Fragment } from "react";
import type { FinanceStatementResponse, FinanceStatementRow } from "@/lib/api";
import { isCalculatedStatementLine } from "./pnl-summary";
import { computeChange, formatPercent, higherIsBetter } from "./variance";

// Shared read-only renderers for the Statement-shaped report JSON served by
// GET /finance/invoicing/reports/{trial-balance,balance-sheet,
// profit-and-loss} (all amounts are integer minor units — cents — never
// floats). Reimplemented for this app; the upstream dubbl report pages under
//  are layout reference only and must not be copied.

function currencyOf(statement: FinanceStatementResponse): string {
  return statement.currency || "USD";
}

function moneyCell(
  cents: number | null | undefined,
  currency: string,
): ReactNode {
  if (cents === null || cents === undefined) {
    return <span className="text-[var(--muted-foreground)]">—</span>;
  }
  return formatMoney({ cents, currency });
}

/** True when the statement carries per-column comparatives (`columns` +
 * `amounts_cents` family) instead of the scalar `amount_cents` family. */
function isComparative(statement: FinanceStatementResponse): boolean {
  return Array.isArray(statement.columns) && statement.columns.length > 0;
}

/** Derived variance cell: absolute change over a percentage. The tone flips
 * for sections where a rise is unwelcome (expenses, liabilities). */
function ChangeCell({
  current,
  base,
  currency,
  favorableWhenUp,
}: {
  current: number | null | undefined;
  base: number | null | undefined;
  currency: string;
  favorableWhenUp: boolean;
}) {
  const change = computeChange(current, base, { favorableWhenUp });
  if (!change) {
    return (
      <TableCell
        key="change"
        className="text-right tabular-nums text-[var(--muted-foreground)]"
      >
        —
      </TableCell>
    );
  }
  const tone =
    change.favorable === null
      ? "text-[var(--muted-foreground)]"
      : change.favorable
        ? "text-[var(--status-success-foreground)]"
        : "text-[var(--danger)]";
  return (
    <TableCell key="change" className="whitespace-nowrap text-right tabular-nums">
      <span className={tone}>
        {change.absoluteCents > 0 ? "+" : ""}
        {formatMoney({ cents: change.absoluteCents, currency })}
      </span>
      <span className="block text-xs text-[var(--muted-foreground)]">
        {formatPercent(change.percent)}
      </span>
    </TableCell>
  );
}

function rowLabelClass(row: FinanceStatementRow): string {
  return row.bold ? "font-semibold" : "";
}

function rowIndent(row: FinanceStatementRow): CSSProperties | undefined {
  if (!row.depth || row.depth <= 0) return undefined;
  return { paddingLeft: `${0.75 + row.depth * 1.25}rem` };
}

/** Statement period used to open the account's postings in the ledger. */
interface DrillRange {
  start_date: string;
  end_date: string;
}

/**
 * Account label for a statement row. Leaf rows carry `account_id`; when a
 * drill range is known the label links into the ledger filtered to that
 * account and period. Synthetic and aggregate rows stay plain text.
 */
function AccountCell({
  row,
  drillRange,
}: {
  row: FinanceStatementRow;
  drillRange?: DrillRange;
}) {
  const label = (
    <>
      {row.code ? (
        <span className="mr-2 font-mono text-xs text-[var(--muted-foreground)]">
          {row.code}
        </span>
      ) : null}
      {row.name}
    </>
  );
  if (!row.account_id || !drillRange) return label;
  return (
    <Link
      to="/journal"
      search={{
        account_id: row.account_id,
        start_date: drillRange.start_date,
        end_date: drillRange.end_date,
      }}
      title="Open this account's postings in the ledger"
      className="underline decoration-dotted underline-offset-4 hover:text-[var(--primary)]"
    >
      {label}
    </Link>
  );
}

export function ReportError({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div
      role="alert"
      className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
    >
      {message}
      {message.includes("No Console organization context") ? (
        <p className="mt-1 text-[var(--muted-foreground)]">
          Accounting follows the organization chosen in SYLVIQ Console. Pick one there and relaunch Accounting.
        </p>
      ) : null}
      <div className="mt-3">
        <Button type="button" variant="secondary" onClick={onRetry}>
          Retry
        </Button>
      </div>
    </div>
  );
}

export function ReportPeriod({
  statement,
}: {
  statement: FinanceStatementResponse;
}) {
  return (
    <p className="mt-1 text-sm text-[var(--muted-foreground)]">
      {statement.period_label} · {currencyOf(statement)}
    </p>
  );
}

// --- Generic amount statement (balance sheet, profit & loss) --------------

export function StatementTable({
  statement,
  emptyMessage,
  grandTotalLabel = "Total",
  drillRange,
}: {
  statement: FinanceStatementResponse;
  emptyMessage: string;
  grandTotalLabel?: string;
  drillRange?: DrillRange;
}) {
  const currency = currencyOf(statement);
  const comparative = isComparative(statement);
  const columns = comparative ? (statement.columns ?? []) : ["Amount"];
  const showChange = comparative && (statement.columns?.length ?? 0) >= 2;
  const leadingCols = columns.length + 1;
  const totalCols = leadingCols + (showChange ? 1 : 0);
  const rowCount = statement.sections.reduce(
    (sum, section) => sum + section.rows.length,
    0,
  );

  return (
    <div className="surface overflow-hidden rounded-2xl">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Account</TableHead>
            {columns.map((column) => (
              <TableHead key={column} className="text-right">
                {column}
              </TableHead>
            ))}
            {showChange ? (
              <TableHead
                className="text-right"
                title="Difference between the first two columns"
              >
                Change
              </TableHead>
            ) : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rowCount === 0 ? (
            <TableRow>
              <TableCell colSpan={totalCols}>{emptyMessage}</TableCell>
            </TableRow>
          ) : (
            statement.sections.map((section) => {
              const calculated = isCalculatedStatementLine(section.label);
              return (
              <Fragment key={`section-${section.label}`}>
                {calculated ? null : (
                <TableRow>
                  <TableCell
                    colSpan={totalCols}
                    className="bg-[var(--muted)] text-xs font-semibold tracking-widest uppercase text-[var(--muted-foreground)]"
                  >
                    {section.label}
                  </TableCell>
                </TableRow>
                )}
                {section.rows.map((row, index) => (
                  <TableRow
                    key={`row-${section.label}-${row.code ?? row.name}-${index}`}
                  >
                    <TableCell
                      className={rowLabelClass(row)}
                      style={rowIndent(row)}
                    >
                      <AccountCell row={row} drillRange={drillRange} />
                    </TableCell>
                    {comparative ? (
                      columns.map((column, i) => (
                        <TableCell
                          key={column}
                          className="text-right tabular-nums"
                        >
                          {moneyCell(row.amounts_cents?.[i], currency)}
                        </TableCell>
                      ))
                    ) : (
                      <TableCell className="text-right tabular-nums">
                        {moneyCell(row.amount_cents, currency)}
                      </TableCell>
                    )}
                    {showChange ? (
                      <ChangeCell
                        current={row.amounts_cents?.[0]}
                        base={row.amounts_cents?.[1]}
                        currency={currency}
                        favorableWhenUp={higherIsBetter(section.label)}
                      />
                    ) : null}
                  </TableRow>
                ))}
                <TableRow key={`subtotal-${section.label}`}>
                  <TableCell className={calculated ? "font-bold" : "font-semibold"}>
                    {calculated ? section.label : `Total ${section.label}`}
                  </TableCell>
                  {comparative ? (
                    columns.map((column, i) => (
                      <TableCell
                        key={column}
                        className="text-right font-semibold tabular-nums"
                      >
                        {moneyCell(section.subtotals_cents?.[i], currency)}
                      </TableCell>
                    ))
                  ) : (
                    <TableCell className="text-right font-semibold tabular-nums">
                      {moneyCell(section.subtotal_cents, currency)}
                    </TableCell>
                  )}
                  {showChange ? (
                    <ChangeCell
                      current={section.subtotals_cents?.[0]}
                      base={section.subtotals_cents?.[1]}
                      currency={currency}
                      favorableWhenUp={higherIsBetter(section.label)}
                    />
                  ) : null}
                </TableRow>
              </Fragment>
              );
            })
          )}
          {(statement.grand_total_cents !== null &&
            statement.grand_total_cents !== undefined) ||
          (statement.grand_totals_cents &&
            statement.grand_totals_cents.length > 0) ? (
            <TableRow key="grand-total">
              <TableCell className="font-bold">{grandTotalLabel}</TableCell>
              {comparative ? (
                columns.map((column, i) => (
                  <TableCell
                    key={column}
                    className="text-right font-bold tabular-nums"
                  >
                    {moneyCell(statement.grand_totals_cents?.[i], currency)}
                  </TableCell>
                ))
              ) : (
                <TableCell className="text-right font-bold tabular-nums">
                  {moneyCell(statement.grand_total_cents, currency)}
                </TableCell>
              )}
              {showChange ? (
                <ChangeCell
                  current={statement.grand_totals_cents?.[0]}
                  base={statement.grand_totals_cents?.[1]}
                  currency={currency}
                  favorableWhenUp
                />
              ) : null}
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </div>
  );
}

// --- Trial balance (debit/credit legs) -------------------------------------

export function TrialBalanceTable({
  statement,
  drillRange,
}: {
  statement: FinanceStatementResponse;
  drillRange?: DrillRange;
}) {
  const currency = currencyOf(statement);
  const comparative = isComparative(statement);
  const columns = comparative ? (statement.columns ?? []) : [];

  // Column debit/credit totals summed across sections (the trial-balance
  // builder emits a single "Accounts" section, but sum generically).
  const columnCount = comparative ? columns.length : 1;
  const totalDebits: Array<number | null> = Array.from(
    { length: columnCount },
    (_, i) =>
      statement.sections.reduce<number | null>((sum, section) => {
        const leg = comparative
          ? section.debit_totals_cents?.[i]
          : section.debit_total_cents;
        if (leg === null || leg === undefined) return sum;
        return (sum ?? 0) + leg;
      }, null),
  );
  const totalCredits: Array<number | null> = Array.from(
    { length: columnCount },
    (_, i) =>
      statement.sections.reduce<number | null>((sum, section) => {
        const leg = comparative
          ? section.credit_totals_cents?.[i]
          : section.credit_total_cents;
        if (leg === null || leg === undefined) return sum;
        return (sum ?? 0) + leg;
      }, null),
  );
  const balanced = totalDebits.map(
    (debit, i) =>
      debit !== null && totalCredits[i] !== null && debit === totalCredits[i],
  );
  const rowCount = statement.sections.reduce(
    (sum, section) => sum + section.rows.length,
    0,
  );
  const debitCreditPairs = comparative ? columnCount * 2 : 2;

  return (
    <div className="grid gap-3">
      <div className="surface overflow-hidden rounded-2xl">
        <Table>
          <TableHeader>
            {comparative ? (
              <TableRow>
                <TableHead rowSpan={2}>Account</TableHead>
                {columns.map((column) => (
                  <TableHead key={column} colSpan={2} className="text-center">
                    {column}
                  </TableHead>
                ))}
              </TableRow>
            ) : null}
            <TableRow>
              {comparative ? null : <TableHead>Account</TableHead>}
              {comparative ? (
                columns.flatMap((column) => [
                  <TableHead key={`${column}-debit`} className="text-right">
                    Debit
                  </TableHead>,
                  <TableHead key={`${column}-credit`} className="text-right">
                    Credit
                  </TableHead>,
                ])
              ) : (
                <>
                  <TableHead className="text-right">Debit</TableHead>
                  <TableHead className="text-right">Credit</TableHead>
                </>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rowCount === 0 ? (
              <TableRow>
                <TableCell colSpan={debitCreditPairs + 1}>
                  No accounts with balances as at this date.
                </TableCell>
              </TableRow>
            ) : (
              statement.sections.map((section) => (
                <Fragment key={`section-${section.label}`}>
                  <TableRow>
                    <TableCell
                      colSpan={debitCreditPairs + 1}
                      className="bg-[var(--muted)] text-xs font-semibold tracking-widest uppercase text-[var(--muted-foreground)]"
                    >
                      {section.label}
                    </TableCell>
                  </TableRow>
                  {section.rows.map((row, index) => (
                    <TableRow key={`row-${row.code ?? row.name}-${index}`}>
                      <TableCell
                        className={rowLabelClass(row)}
                        style={rowIndent(row)}
                      >
                        <AccountCell row={row} drillRange={drillRange} />
                      </TableCell>
                      {comparative ? (
                        columns.flatMap((column, i) => [
                          <TableCell
                            key={`${column}-debit`}
                            className="text-right tabular-nums"
                          >
                            {moneyCell(row.debits_cents?.[i], currency)}
                          </TableCell>,
                          <TableCell
                            key={`${column}-credit`}
                            className="text-right tabular-nums"
                          >
                            {moneyCell(row.credits_cents?.[i], currency)}
                          </TableCell>,
                        ])
                      ) : (
                        <>
                          <TableCell className="text-right tabular-nums">
                            {moneyCell(row.debit_cents, currency)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {moneyCell(row.credit_cents, currency)}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                  <TableRow key={`subtotal-${section.label}`}>
                    <TableCell className="font-semibold">
                      Total {section.label}
                    </TableCell>
                    {comparative ? (
                      columns.flatMap((column, i) => [
                        <TableCell
                          key={`${column}-debit`}
                          className="text-right font-semibold tabular-nums"
                        >
                          {moneyCell(section.debit_totals_cents?.[i], currency)}
                        </TableCell>,
                        <TableCell
                          key={`${column}-credit`}
                          className="text-right font-semibold tabular-nums"
                        >
                          {moneyCell(
                            section.credit_totals_cents?.[i],
                            currency,
                          )}
                        </TableCell>,
                      ])
                    ) : (
                      <>
                        <TableCell className="text-right font-semibold tabular-nums">
                          {moneyCell(section.debit_total_cents, currency)}
                        </TableCell>
                        <TableCell className="text-right font-semibold tabular-nums">
                          {moneyCell(section.credit_total_cents, currency)}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                </Fragment>
              ))
            )}
            {rowCount > 0 ? (
              <TableRow key="grand-total">
                <TableCell className="font-bold">Total</TableCell>
                {totalDebits.flatMap((debit, i) => {
                  const label = comparative ? columns[i] : "total";
                  return [
                    <TableCell
                      key={`total-debit-${label}`}
                      className="text-right font-bold tabular-nums"
                    >
                      {moneyCell(debit, currency)}
                    </TableCell>,
                    <TableCell
                      key={`total-credit-${label}`}
                      className="text-right font-bold tabular-nums"
                    >
                      {moneyCell(totalCredits[i], currency)}
                    </TableCell>,
                  ];
                })}
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </div>
      {rowCount > 0
        ? balanced.map((ok, i) => (
            <p
              key={balanced.length > 1 ? columns[i] : "balance"}
              className={`text-sm ${ok ? "text-[var(--muted-foreground)]" : "font-semibold text-[var(--danger)]"}`}
              role={ok ? undefined : "alert"}
            >
              {comparative ? `${columns[i]}: ` : ""}
              {ok
                ? "Total debits equal total credits — the books balance."
                : "Total debits do NOT equal total credits — the books are out of balance."}
            </p>
          ))
        : null}
    </div>
  );
}
